/**
 * API Details Step Page Component
 *
 * This component combines the APIDetailsStep form with BrokerInstructions
 * in a single page layout to prevent re-rendering issues when app name changes.
 *
 * By isolating this step in its own component, we can ensure smooth updates
 * without causing the entire page layout to re-render.
 */

import React, { memo, useMemo, useCallback, useState } from "react";
import APIDetailsStep from "./APIDetailsStepForm";
import BrokerInstructions from "../Components/BrokerInstructions";

const APIDetailsStepPage = ({
  isEditMode,
  accountData,
  availableBrokers,
  selectedBroker,
  onBrokerSelect,
  onSubmit,
  onCancel,
  loading,
  appName,
  onAppNameChange,
}) => {
  // Memoize the broker object to prevent unnecessary re-renders
  const memoizedBroker = useMemo(() => {
    if (!selectedBroker) return null;
    return {
      ...selectedBroker,
      broker_id: selectedBroker.id, // Map for compatibility
      account_id: accountData?.account_id, // Include account_id for edit mode
    };
  }, [selectedBroker, accountData?.account_id]);

  // Memoize callback functions to prevent re-renders
  const handleContinue = useCallback(() => {}, []);
  // const handleAccountIdChange = useCallback(() => {}, []);
  // Update the handleAccountIdChange to capture the account ID
  const [redirectAccountId, setRedirectAccountId] = useState(null);
  const handleAccountIdChange = useCallback((accountId) => {
    console.log("Received account ID from redirect URL:", accountId);
    setRedirectAccountId(accountId);
  }, []);

  // Create a wrapper for onSubmit that includes the account ID
  const handleSubmit = useCallback(
    (formData) => {
      // If we have a redirect account ID, include it in the submission
      const enhancedData = {
        ...formData,
      };
      
      // Add the account_id if available from redirect URL
      if (redirectAccountId) {
        enhancedData.account_id = redirectAccountId;
        console.log("Including account ID in submission:", redirectAccountId);
      }
      console.log("Submitting enhanced data:", enhancedData);
      // Call the original onSubmit with enhanced data
      onSubmit(enhancedData);
    },
    [onSubmit, redirectAccountId]
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
      {/* Left Side - API Details Form (50%) */}
      <div className="lg:col-span-1">
        <div className="bg-bg-2 rounded-lg shadow-sm dark:shadow-gray-900/30 border border-[var(--divider)] p-4 sm:p-6 lg:p-8 transition-all hover:shadow-md dark:hover:shadow-gray-900/40 h-fit">
          <div className="border-b border-[var(--divider)] pb-4 mb-6">
            <h3 className="ds-h3 sm: lg: text-fg-1 flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
              API Details
            </h3>
            <p className="text-xs sm:text-sm lg:text-base text-fg-2 mt-2">
              {isEditMode ? "Update " : "Enter "}
              your {selectedBroker?.display_name || selectedBroker?.name} API
              credentials
            </p>
          </div>
          <APIDetailsStep
            isEditMode={isEditMode}
            accountData={accountData}
            availableBrokers={availableBrokers}
            selectedBroker={selectedBroker}
            onBrokerSelect={onBrokerSelect}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            loading={loading}
            appName={appName}
            onAppNameChange={onAppNameChange}
          />
        </div>
      </div>

      {/* Right Side - Instructions (50%) */}
      <div className="lg:col-span-1">
        <div className="bg-bg-2 rounded-lg shadow-sm dark:shadow-gray-900/30 border border-[var(--divider)] p-4 sm:p-6 lg:p-8 sticky top-4 sm:top-6 lg:top-8 transition-all h-fit">
          <div className="border-b border-[var(--divider)] pb-4 mb-6">
            <h3 className="ds-h3 sm: lg: text-fg-1 flex items-center">
              <span className="w-2 h-2 bg-cyan-500 rounded-full mr-3"></span>
              Setup Instructions
            </h3>
            <p className="text-xs sm:text-sm lg:text-base text-fg-2 mt-2">
              Follow these steps to connect your{" "}
              {selectedBroker?.display_name || selectedBroker?.name} account
            </p>
          </div>
          <BrokerInstructions
            broker={memoizedBroker}
            onContinue={handleContinue}
            userId="user"
            app_name={appName}
            onAccountIdChange={handleAccountIdChange}
            isEditMode={isEditMode}
          />
        </div>
      </div>
    </div>
  );
};

// Custom comparison function for React.memo to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
  // Compare primitive props
  if (
    prevProps.isEditMode !== nextProps.isEditMode ||
    prevProps.loading !== nextProps.loading ||
    prevProps.appName !== nextProps.appName
  ) {
    return false;
  }

  // Compare selectedBroker by id to avoid deep comparison
  const prevBrokerId = prevProps.selectedBroker?.id;
  const nextBrokerId = nextProps.selectedBroker?.id;
  if (prevBrokerId !== nextBrokerId) {
    return false;
  }

  // Compare function references (these should be stable from parent)
  if (
    prevProps.onBrokerSelect !== nextProps.onBrokerSelect ||
    prevProps.onSubmit !== nextProps.onSubmit ||
    prevProps.onCancel !== nextProps.onCancel ||
    prevProps.onAppNameChange !== nextProps.onAppNameChange
  ) {
    return false;
  }

  // Compare arrays by length and basic content
  if (
    prevProps.availableBrokers?.length !== nextProps.availableBrokers?.length
  ) {
    return false;
  }

  // Compare accountData by reference (should be stable)
  if (prevProps.accountData !== nextProps.accountData) {
    return false;
  }

  return true;
};

APIDetailsStepPage.displayName = "APIDetailsStepPage";

export default memo(APIDetailsStepPage, arePropsEqual);
